import {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {diApiNa} from '@di-shared/api/di';
import type {ApiResponseLatestScoreResponse, ApiResponseMonthlyTrendResponse, ApiResponseTermsConsentStatus} from '@di-shared/api/di/__generated_na';
import {ERROR} from '@di-shared/const';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import ScrollToTopOnMount from '@di-shared/features/common/ScrollToTopOnMount';
import VariableDonutChart from '@di-shared/features/common/VariableDonutChart';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import useAvailableComponentNa from '@di-shared/features/home/na/useAvailableComponent.na';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import SafeDrivingSummaryGraphEu from '@di-shared/features/safe-driving-LN/SafeDrivingSummaryGraph/.eu';
import SafeDrivingDailyGraphNa, {
  SafeDrivingDailyGraphNaError,
  SafeDrivingDailyGraphNaLoading,
} from '@di-shared/features/safe-driving-LN/report/SafeDrivingDailyGraph/.na';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {differenceInDay, parseDateWithFormat, subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQueries} from '@tanstack/react-query';

const MAX_AGREE_DAYS = 187;
const MONTHLY_GRAPH_PAGE_SIZE = 6;
const SCORE_THRESHOLD = {
  CAUTION_WARNING: 74,
  GOOD_CAUTION: 88,
};

export interface SafeDrivingContentsLNNaProps {
  vin: string;
}

export default function ({vin}: SafeDrivingContentsLNNaProps) {
  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <ErrorBoundary fallback={<SafeDrivingContentsErrorNa vin={vin} />}>
        <Suspense fallback={<SafeDrivingContentsLoadingNa vin={vin} />}>
          <Inner vin={vin} />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

function Inner({vin}: SafeDrivingContentsLNNaProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();
  const {isUbiAvailable} = useAvailableComponentNa({vin});
  const resolveFeatureFlag = useFirebaseStore((state) => state.resolveFeatureFlag);
  const isUbiBannerVisible = resolveFeatureFlag('NA_UBI', vin, {serverSpecVisible: isUbiAvailable});

  const [{data: latestData}, {data: monthlyTrendData}, {data: termsData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApiNa.safeDriving.latest), vin],
        queryFn: () => diApiNa.safeDriving.latest({vin}),
        select: ({data}: ApiResponseLatestScoreResponse) => ({
          ...data,
          latestScoreInfo: {
            ...data.latestScoreInfo,
            lastUpdatedDate: parseDateWithFormat(data.latestScoreInfo.lastUpdatedDate),
          },
        }),
      },
      {
        queryKey: [getQueryKey(diApiNa.safeDriving.monthlyTrend1), vin],
        queryFn: () => diApiNa.safeDriving.monthlyTrend1({vin}),
        select: ({data}: ApiResponseMonthlyTrendResponse) => data,
      },
      {
        queryKey: [getQueryKey(diApiNa.vehicle.getConsentedSelectiveTermsCode), vin, 'SAFETY_SCORE'],
        queryFn: () =>
          diApiNa.vehicle.getConsentedSelectiveTermsCode({
            vin,
            termscode: 'SAFETY_SCORE',
          }),
        select: ({data}: ApiResponseTermsConsentStatus) => ({
          ...data,
          consentDate: parseDateWithFormat(data.consentDate),
        }),
      },
    ],
  });

  const monthlyScoreResponseList = monthlyTrendData.monthlyScoreResponseList || [];
  const activeMonth = monthlyScoreResponseList[monthlyScoreResponseList.length - 1]?.month;
  const totalPages = Math.max(1, Math.ceil(monthlyScoreResponseList.length / MONTHLY_GRAPH_PAGE_SIZE));
  const [page, setPage] = useState(totalPages - 1);
  const pageItems = monthlyScoreResponseList.slice(
    page * MONTHLY_GRAPH_PAGE_SIZE,
    page * MONTHLY_GRAPH_PAGE_SIZE + MONTHLY_GRAPH_PAGE_SIZE
  );
  const monthlyDiff =
    monthlyScoreResponseList.length > 1
      ? monthlyScoreResponseList[monthlyScoreResponseList.length - 1].score -
        monthlyScoreResponseList[monthlyScoreResponseList.length - 2].score
      : 0;

  const dDay =
    termsData.consentDate && latestData.latestScoreInfo.lastUpdatedDate
      ? Math.min(differenceInDay(latestData.latestScoreInfo.lastUpdatedDate, termsData.consentDate) + 1, MAX_AGREE_DAYS)
      : null;

  const filterFrom = subDate(today, {days: 6});

  async function openGuide() {
    await ccWebviewUtils.openBottomSheetWebViewUrl<boolean>(ROUTE_PATHS.BOTTOM_SHEET_SAFE_DRIVING_GUIDE.getFullPath(), {
      fromDate:
        latestData.latestScoreInfo.lastUpdatedDate && termsData.consentDate
          ? differenceInDay(latestData.latestScoreInfo.lastUpdatedDate, termsData.consentDate) > MAX_AGREE_DAYS - 1
            ? CldrDatetimeFormatter.format(subDate(latestData.latestScoreInfo.lastUpdatedDate, {days: MAX_AGREE_DAYS - 1}), 'yMMMd')
            : CldrDatetimeFormatter.format(termsData.consentDate, 'yMMMd')
          : '',
      toDate: latestData.latestScoreInfo.lastUpdatedDate
        ? CldrDatetimeFormatter.format(latestData.latestScoreInfo.lastUpdatedDate, 'yMMMd')
        : '',
    });
  }

  return (
    <>
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('ctn_dss_LN_driving_safety_score')}</h1>
        <div className="nav_box">
          <button
            type="button"
            className="btn btn_ic"
            aria-label={t('ctn_dss_open_tooltip')}
            onClick={openGuide}
          >
            <span
              className="ic ic_information_20px"
              aria-hidden="true"
            ></span>
          </button>
        </div>
      </Header>

      <main
        id="content"
        className="content page_safe_driving"
      >
        <section className="section section_gb_safe_driving">
          <h2 className="blind">my safe driving score</h2>
          <div className="section_heading type_reverse">
            <h3 className="section_heading_title">{t('ctn_dss_LN_based_on_history')}</h3>
            <p className="section_heading_desc">{dDay !== null && <span className="gb_score_day label_large_b">D+{dDay}</span>}</p>
          </div>

          <SafeDrivingSummaryGraphEu
            score={latestData.latestScoreInfo.drivingSafetyScore}
            scoreDiff={latestData.latestScoreInfo.previousScoreDelta}
          />
          <div
            className="section_container_caption my_score_update"
            style={{marginBottom: 40}}
          >
            {t('cts_dss_LN_last_updated')}&nbsp;
            {latestData.latestScoreInfo.lastUpdatedDate
              ? CldrDatetimeFormatter.format(latestData.latestScoreInfo.lastUpdatedDate, 'yMMMaHM')
              : CldrDatetimeFormatter.format(today, 'yMMMaHM')}
          </div>

          <div className="section_container">
            <div className="gb_score_filter">
              <button
                type="button"
                className="btn_select_day"
                disabled
              >
                <span className="body_small_r">{t('ctn_dss_LN_last_7days')}</span>
                <span
                  className="ic ic_arrow_down_12px"
                  aria-hidden="true"
                ></span>
              </button>
              <span className="gb_score_filter_range body_small_r">
                {CldrDatetimeFormatter.format(filterFrom, 'yMMMd')} - {CldrDatetimeFormatter.format(today, 'yMMMd')}
              </span>
            </div>
            <div className="score_factor">
              <ul className="score_factor_list">
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_LN_average_trip_speed')}</span>
                  <span className="score_factor_value">{latestData.latestScoreInfo.subScoreDetailResponse.averageTripSpeed}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_LN_hard_braking_events')}</span>
                  <span className="score_factor_value">{latestData.latestScoreInfo.subScoreDetailResponse.hardBraking}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_LN_highway_speeding_events')}</span>
                  <span className="score_factor_value">{latestData.latestScoreInfo.subScoreDetailResponse.maximumHighSpeed}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_LN_time_of_day_driving')}</span>
                  <span className="score_factor_value">{latestData.latestScoreInfo.subScoreDetailResponse.drivingTimeOfDay}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="section_container safe_driving_monthy">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('ctn_dss_LN_monthly_driving_safety_score')}</h3>
              {monthlyDiff !== 0 && (
                <div className="score_point_info">
                  <span className={clsx('score_point', monthlyDiff > 0 ? 'type_up' : 'type_down')}>
                    <span
                      className={clsx('ic', monthlyDiff > 0 ? 'ic_di_count_up' : 'ic_di_count_down')}
                      aria-hidden="true"
                    >
                      <span className="blind">{monthlyDiff > 0 ? 'up' : 'down'}</span>
                    </span>
                    <span className="score_value">{t('ctn_dss_LN_points_$score', {score: Math.abs(monthlyDiff)})}</span>
                  </span>
                </div>
              )}
            </div>
            <div className="gb_monthly_graph_scroll_wrap">
              <div className="gb_monthly_graph_area">
                <div className="bar_graph">
                  {pageItems.map((item) => {
                    const parsedMonth = parseDateWithFormat(item.month);
                    return (
                      <div
                        className={clsx('bar_graph_item', {
                          'is-active': item.month === activeMonth,
                          type_warning: item.score <= SCORE_THRESHOLD.CAUTION_WARNING,
                          type_caution: item.score > SCORE_THRESHOLD.CAUTION_WARNING && item.score <= SCORE_THRESHOLD.GOOD_CAUTION,
                          type_good: item.score > SCORE_THRESHOLD.GOOD_CAUTION,
                        })}
                        key={item.month}
                      >
                        {item.month === activeMonth && <span className="speech_bubble">{item.score}</span>}
                        <span
                          className="graph_bar"
                          style={{height: `${0.9 * item.score}px`}}
                        ></span>
                        <span className="graph_title">{parsedMonth ? CldrDatetimeFormatter.format(parsedMonth, 'MMM') : ''}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="arrow_area">
                {page > 0 && (
                  <button
                    type="button"
                    className="btn_prev"
                    onClick={() => setPage((prev) => prev - 1)}
                  >
                    <span
                      className="ic ic_arrow_back_16px"
                      aria-hidden="true"
                    >
                      <span className="blind">{t('ctn_dss_monthly_chart_before')}</span>
                    </span>
                  </button>
                )}
                {page < totalPages - 1 && (
                  <button
                    type="button"
                    className="btn_next"
                    onClick={() => setPage((prev) => prev + 1)}
                  >
                    <span
                      className="ic ic_arrow_forward_16px"
                      aria-hidden="true"
                    >
                      <span className="blind">{t('ctn_dss_monthly_chart_next')}</span>
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>

          <SafeDrivingDailyGraphNa
            key={activeMonth}
            vin={vin}
            selectedMonth={activeMonth}
          />

          <div className="driving_banner type_square">
            <LinkTo
              to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
              className="driving_banner_box"
            >
              <span className="driving_banner_txtbox type_reverse">
                <strong className="driving_banner_title body_large_b">{t('ctn_mytrips_title')}</strong>
                <span className="driving_banner_desc body_small_r">{t('ctn_mytrips_tab')}</span>
              </span>
              <span className="driving_banner_imgbox">
                <ImageSelector
                  className="driving_banner_img"
                  width="80"
                  height="80"
                  alt="driving_banner_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_my_trips.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_my_trips.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_my_trips.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_my_trips.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_my_trips.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_my_trips.png')}
                />
              </span>
            </LinkTo>
          </div>

          {isUbiBannerVisible && (
            <div className="driving_banner type_square">
              <LinkTo
                to={ROUTE_PATHS.HOME_BENEFIT_TAB.getFullPath(vin)}
                className="driving_banner_box"
              >
                <span className="driving_banner_txtbox">
                  <strong className="driving_banner_title body_xlarge_b">{t('ctn_ubi_dss_banner_title_eu')}</strong>
                  <span className="driving_banner_desc body_medium_r">{t('ctn_ubi_dss_banner_message_eu')}</span>
                </span>
                <span className="driving_banner_imgbox">
                  <ImageSelector
                    className="driving_banner_img"
                    alt="driving_banner_img"
                    kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_insure_benefit.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_insure_benefit.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_insure_benefit.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_insure_benefit.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_insure_benefit.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_insure_benefit.png')}
                  />
                </span>
              </LinkTo>
            </div>
          )}
        </section>
      </main>
    </>
  );
}

function SafeDrivingContentsErrorNa({vin}: SafeDrivingContentsLNNaProps) {
  const {t} = useInferableTranslation();

  return (
    <>
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('ctn_dss_LN_driving_safety_score')}</h1>
        <div className="nav_box"></div>
      </Header>
      <main
        id="content"
        className="content page_safe_driving"
      >
        <section className="section section_gb_safe_driving">
          <div className="section_heading type_reverse">
            <h3 className="section_heading_title">{t('ctn_dss_LN_based_on_history')}</h3>
          </div>
          <div className="driving_score_graph_area">
            <VariableDonutChart
              color={''}
              ratio={0}
              width={190}
              strokeWidth={10}
              angle={45}
            />
            <div className="driving_mileage_graph_info">
              <span className="driving_mileage_graph_score">
                <span className="score_value">{ERROR.DASH}</span>
              </span>
              <span className="driving_mileage_graph_compare">
                <span className="compare_value type_plus">{ERROR.DASH}</span>
                <span className="compare_text">{t('ctn_home_dss_points')}</span>
              </span>
            </div>
          </div>
          <div
            className="section_container_caption my_score_update"
            style={{marginBottom: 40}}
          >
            {t('cts_dss_LN_last_updated')}&nbsp;{ERROR.DASH}
          </div>
          <div className="section_container">
            <div className="score_factor">
              <ul className="score_factor_list type_error">
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_LN_average_trip_speed')}</span>
                  <span className="score_factor_value">{ERROR.DASH}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_LN_hard_braking_events')}</span>
                  <span className="score_factor_value">{ERROR.DASH}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_LN_highway_speeding_events')}</span>
                  <span className="score_factor_value">{ERROR.DASH}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_LN_time_of_day_driving')}</span>
                  <span className="score_factor_value">{ERROR.DASH}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="section_container safe_driving_monthy">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('ctn_dss_LN_monthly_driving_safety_score')}</h3>
            </div>
            <div className="gb_monthly_graph_scroll_wrap">
              <div className="gb_monthly_graph_area">
                <div className="bar_graph">
                  {Array.from({length: 6}).map((_, index) => (
                    <div
                      className="bar_graph_item"
                      key={index}
                    >
                      <span
                        className="graph_bar"
                        style={{height: '0%'}}
                      ></span>
                      <span className="graph_title">{ERROR.DASH}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <SafeDrivingDailyGraphNaError />

          <div className="driving_banner type_square">
            <LinkTo
              to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
              className="driving_banner_box"
            >
              <span className="driving_banner_txtbox type_reverse">
                <strong className="driving_banner_title body_large_b">{t('ctn_mytrips_title')}</strong>
                <span className="driving_banner_desc body_small_r">{t('ctn_mytrips_tab')}</span>
              </span>
              <span className="driving_banner_imgbox">
                <ImageSelector
                  className="driving_banner_img"
                  width="80"
                  height="80"
                  alt="driving_banner_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_my_trips.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_my_trips.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_my_trips.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_my_trips.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_my_trips.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_my_trips.png')}
                />
              </span>
            </LinkTo>
          </div>
        </section>
      </main>
    </>
  );
}

function SafeDrivingContentsLoadingNa({vin}: SafeDrivingContentsLNNaProps) {
  const {t} = useInferableTranslation();

  return (
    <>
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('ctn_dss_LN_driving_safety_score')}</h1>
        <div className="nav_box"></div>
      </Header>
      <main
        id="content"
        className="content page_safe_driving"
      >
        <section className="section section_gb_safe_driving">
          <div className="section_heading type_reverse skeleton">
            <h3 className="section_heading_title">{t('ctn_dss_LN_based_on_history')}</h3>
            <p className="section_heading_desc">
              <span className="gb_score_day label_large_b">D+187</span>
            </p>
          </div>
          <div className="driving_score_graph_area skeleton">
            <VariableDonutChart
              color={''}
              ratio={0}
              width={0}
              strokeWidth={10}
              angle={45}
            />
          </div>
          <div
            className="section_container_caption my_score_update skeleton"
            style={{marginBottom: 40}}
          >
            {t('cts_dss_LN_last_updated')}&nbsp;{'22 May 2026, 04:06'}
          </div>
          <div className="section_container skeleton">
            <div className="score_factor">
              <ul className="score_factor_list">
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_LN_average_trip_speed')}</span>
                  <span className="score_factor_value">7</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_LN_hard_braking_events')}</span>
                  <span className="score_factor_value">3</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_LN_highway_speeding_events')}</span>
                  <span className="score_factor_value">1</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_LN_time_of_day_driving')}</span>
                  <span className="score_factor_value">10</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="section_container safe_driving_monthy skeleton">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('ctn_dss_LN_monthly_driving_safety_score')}</h3>
            </div>
            <div className="gb_monthly_graph_scroll_wrap">
              <div className="gb_monthly_graph_area">
                <div className="bar_graph">
                  {Array.from({length: 6}).map((_, index) => (
                    <div
                      className="bar_graph_item"
                      key={index}
                    >
                      <span
                        className="graph_bar"
                        style={{height: `${40 + index * 8}px`}}
                      ></span>
                      <span className="graph_title">{'-'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <SafeDrivingDailyGraphNaLoading />

          <div className="driving_banner type_square skeleton">
            <LinkTo
              to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
              className="driving_banner_box"
            >
              <span className="driving_banner_txtbox type_reverse">
                <strong className="driving_banner_title body_large_b">{t('ctn_mytrips_title')}</strong>
                <span className="driving_banner_desc body_small_r">{t('ctn_mytrips_tab')}</span>
              </span>
              <span className="driving_banner_imgbox">
                <ImageSelector
                  className="driving_banner_img"
                  width="80"
                  height="80"
                  alt="driving_banner_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_my_trips.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_my_trips.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_my_trips.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_my_trips.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_my_trips.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_my_trips.png')}
                />
              </span>
            </LinkTo>
          </div>
        </section>
      </main>
    </>
  );
}
